import storage from '../../../../../hooks/useStorage/useStorage';
import { OfflineDatabasePriceTable } from '../../../database/offlineDatabase/types/offlineDatabasePriceTable.types';
import { OfflineOrder } from '../../../domain/offlineOrder/OfflineOrder';
import { offlineCustomersRepository } from '../../../repositories/offlineCustomers.repository';
import { offlinePriceTablesRepository } from '../../../repositories/offlinePriceTables.repository';
import { offlineProductColorPriceInfoRepository } from '../../../repositories/offlineProductColorPriceInfo.repository';
import { offlineSalesRepresentativeRepository } from '../../../repositories/offlineSalesRepresentative.repository';
import { offlineSellersRepository } from '../../../repositories/offlineSellers.repository';
import { offlineUsersRepository } from '../../../repositories/offlineUsers.repository';
import {
  validateIfCustomerHasAccessToOrderProducts,
  validateSellerCanCreateOrUpdateOrderWithoutCustomer,
} from './offlineOrder.utils';
import { offlineOrderArtifactsGenerator } from './offlineOrderArtifactsGenerator.util';
import { OfflineOrderChangeDataHandlerChangeCustomerParams } from './types/offlineOrderChangeDataHandler.util.types';

export const offlineOrderChangeDataHandler = {
  changePriceTable: async (
    order: OfflineOrder,
    priceTableId: string,
    loadedPriceTable?: OfflineDatabasePriceTable,
  ) => {
    const priceTable = loadedPriceTable
      ? loadedPriceTable
      : await offlinePriceTablesRepository.getById(priceTableId);

    if (!priceTable) {
      throw new Error('price table not found');
    }

    const orderProducts = order.products;

    if (!orderProducts || orderProducts.length === 0) {
      order.setPriceTable(priceTable);
      return;
    }

    const productColorIds = orderProducts.map((product) => product.productColor.id);

    const productColorsPriceInfo =
      await offlineProductColorPriceInfoRepository.findByProductColorIds(
        productColorIds,
        priceTable.id,
      );

    order.setPriceTable(priceTable, { productsPriceInfo: productColorsPriceInfo });
  },
  changeOrderCustomer: async ({
    order,
    customerId,
    blockOrderWithoutCustomer,
  }: OfflineOrderChangeDataHandlerChangeCustomerParams) => {
    const customer = customerId ? await offlineCustomersRepository.getById(customerId) : undefined;

    const teamId = storage.getTeamId();
    const userTeam = offlineUsersRepository.getUserTeam(teamId);
    if (!userTeam) {
      throw new Error('team not found');
    }

    validateSellerCanCreateOrUpdateOrderWithoutCustomer({
      blockOrderWithoutCustomer,
      customerId,
    });

    if (customer) {
      await validateIfCustomerHasAccessToOrderProducts({
        customerId: customer.id,
        orderId: order.id,
      });

      const salesRepresentative = await offlineOrderArtifactsGenerator.getSalesRepForOrder(
        customer.id,
      );

      const customerPriceTable = await offlinePriceTablesRepository.getById(customer.priceTableId);

      const priceTable = customerPriceTable || (await offlinePriceTablesRepository.getDefault());

      if (!priceTable) {
        throw new Error('price table not found');
      }

      if (!!salesRepresentative) {
        await offlineOrderChangeDataHandler.changeSalesReps(order, salesRepresentative.id);
      }

      await offlineOrderChangeDataHandler.changePriceTable(order, priceTable.id, priceTable);
    }

    order.setCustomer(customer);
  },
  changeSalesReps: async (order: OfflineOrder, salesRepId?: string | null) => {
    if (!salesRepId) {
      order.setSalesRepresentative(undefined);
      return;
    }

    const salesRepresentative = await offlineSalesRepresentativeRepository.getById(salesRepId);

    if (!salesRepresentative && order.customer?.id) {
      const salesRepFromOrderCustomer = await offlineOrderArtifactsGenerator.getSalesRepForOrder(
        order.customer.id,
      );

      order.setSalesRepresentative(salesRepFromOrderCustomer);
      return;
    }

    order.setSalesRepresentative(salesRepresentative);
  },
  changeSeller: async (order: OfflineOrder, sellerId?: string | null) => {
    const seller = sellerId ? await offlineSellersRepository.getById(sellerId) : undefined;
    order.setSeller(seller);
  },
};
